/**
 * Skin previews, on a 2D canvas.
 *
 * ONE description of what a skin looks like outside the arena, imported by both
 * the shop and the lobby. There were two, and within a day they had drifted in
 * six of the twenty four skins: the basketball lost its seams, the eight ball
 * drew a different mark entirely, and the emoji were sized differently. A
 * preview that lies about what you are buying is worse than no preview.
 *
 * The arena itself draws with PixiJS (skinRender.ts) because it needs a scene
 * graph; this is the flat version for thumbnails.
 */
import { skinById, type SkinDef } from '@arena/games';

/** Draw one skin into a small canvas. Mirrors skinRender.ts, in 2D context. */
export function paintSkin(canvas: HTMLCanvasElement, skin: SkinDef | null, size: number): void {
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  canvas.width = canvas.height = Math.round(size * dpr);
  canvas.style.width = canvas.style.height = `${size}px`;
  const g = canvas.getContext('2d')!;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, size, size);

  const c = size / 2;
  const r = size * 0.34;
  const hex = (v: number): string => `#${v.toString(16).padStart(6, '0')}`;

  // The halo first, so the body sits inside its own light.
  if (skin) {
    const grad = g.createRadialGradient(c, c, r * 0.4, c, c, r * 2);
    grad.addColorStop(0, `${hex(skin.glow)}66`);
    grad.addColorStop(1, `${hex(skin.glow)}00`);
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
  }

  if (!skin) {
    g.beginPath();
    g.arc(c, c, r, 0, Math.PI * 2);
    g.fillStyle = '#22d3ee';
    g.fill();
    g.lineWidth = 2;
    g.strokeStyle = 'rgba(255,255,255,0.35)';
    g.stroke();
    return;
  }

  g.beginPath();
  g.arc(c, c, r, 0, Math.PI * 2);
  g.fillStyle = hex(skin.base);
  g.fill();

  g.lineWidth = Math.max(2, r * 0.12);
  g.strokeStyle = hex(skin.accent);

  if (skin.kind === 'ball') {
    switch (skin.pattern) {
      case 'tennis':
        g.beginPath();
        g.moveTo(c - r, c - r * 0.35);
        g.quadraticCurveTo(c, c, c - r, c + r * 0.35);
        g.moveTo(c + r, c - r * 0.35);
        g.quadraticCurveTo(c, c, c + r, c + r * 0.35);
        g.stroke();
        break;
      case 'basketball':
        g.beginPath();
        g.moveTo(c - r, c);
        g.lineTo(c + r, c);
        g.moveTo(c, c - r);
        g.lineTo(c, c + r);
        g.stroke();
        break;
      case 'football':
        g.beginPath();
        for (let i = 0; i < 5; i++) {
          const a = -Math.PI / 2 + (i / 5) * Math.PI * 2;
          const x = c + Math.cos(a) * r * 0.42;
          const y = c + Math.sin(a) * r * 0.42;
          if (i === 0) g.moveTo(x, y);
          else g.lineTo(x, y);
        }
        g.closePath();
        g.fillStyle = hex(skin.accent);
        g.fill();
        break;
      case 'beach': {
        const hues = [hex(skin.accent), '#22d3ee', '#facc15', '#4ade80'];
        for (let i = 0; i < 6; i += 2) {
          g.beginPath();
          g.moveTo(c, c);
          g.arc(c, c, r, (i / 6) * Math.PI * 2, (i / 6) * Math.PI * 2 + Math.PI / 3);
          g.closePath();
          g.fillStyle = hues[(i / 2) % hues.length];
          g.fill();
        }
        break;
      }
      case 'eight':
        g.beginPath();
        g.arc(c, c, r * 0.52, 0, Math.PI * 2);
        g.fillStyle = hex(skin.accent);
        g.fill();
        g.fillStyle = hex(skin.base);
        g.font = `700 ${r * 0.7}px system-ui, sans-serif`;
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        g.fillText('8', c, c + r * 0.04);
        break;
      case 'bowling':
        g.fillStyle = hex(skin.accent);
        for (const [dx, dy] of [
          [-0.3, -0.28],
          [0.06, -0.36],
          [-0.1, 0.06],
        ]) {
          g.beginPath();
          g.arc(c + dx * r, c + dy * r, r * 0.14, 0, Math.PI * 2);
          g.fill();
        }
        break;
    }
  } else if (skin.kind === 'face') {
    const eye = r * 0.16;
    const ex = r * 0.36;
    const ey = c - r * 0.2;
    g.fillStyle = hex(skin.accent);
    if (skin.mood === 'cool') {
      g.fillRect(c - r * 0.62, ey - eye, r * 1.24, eye * 2.1);
    } else if (skin.mood === 'sleepy') {
      g.beginPath();
      g.moveTo(c - ex - eye, ey);
      g.lineTo(c - ex + eye, ey);
      g.moveTo(c + ex - eye, ey);
      g.lineTo(c + ex + eye, ey);
      g.stroke();
    } else {
      g.beginPath();
      g.arc(c - ex, ey, eye, 0, Math.PI * 2);
      g.fill();
      if (skin.mood === 'wink') {
        g.beginPath();
        g.moveTo(c + ex - eye, ey);
        g.lineTo(c + ex + eye, ey);
        g.stroke();
      } else {
        g.beginPath();
        g.arc(c + ex, ey, eye, 0, Math.PI * 2);
        g.fill();
      }
    }
    g.beginPath();
    const my = c + r * 0.22;
    if (skin.mood === 'angry') {
      g.moveTo(c - r * 0.34, my + r * 0.12);
      g.quadraticCurveTo(c, my - r * 0.16, c + r * 0.34, my + r * 0.12);
    } else {
      g.moveTo(c - r * 0.38, my - r * 0.06);
      g.quadraticCurveTo(c, my + r * 0.32, c + r * 0.38, my - r * 0.06);
    }
    g.stroke();
  } else if (skin.kind === 'emoji') {
    g.font = `${r * 1.5}px system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(skin.glyph ?? '?', c, c + r * 0.06);
  }

  g.beginPath();
  g.arc(c, c, r, 0, Math.PI * 2);
  g.lineWidth = 2;
  g.strokeStyle = 'rgba(255,255,255,0.3)';
  g.stroke();
}


/** Convenience for callers that hold a skin id rather than a definition. */
export function paintSkinPreview(canvas: HTMLCanvasElement, id: string, size: number): void {
  paintSkin(canvas, skinById(id), size);
}
